import { getErrorMessage } from '@/utils/errorUtils';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlusCircle, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "@/hooks/use-toast";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { TeeBox } from "@/types";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

// Define the validation schema
const courseFormSchema = z.object({
  name: z.string().min(3, { message: "Course name must be at least 3 characters" }),
  location: z.string().min(3, { message: "Location must be at least 3 characters" }),
  holesCount: z.number().int().min(9).max(27).default(18),
  par: z.number().int().min(27).max(108).optional(),
  availableTees: z.array(z.enum(["red", "gold", "white", "blue", "black"] as const)).default(["red", "white", "blue"])
});

type CourseFormValues = z.infer<typeof courseFormSchema>;

interface AddCourseDialogProps {
  onAddCourse: () => void;
  onCourseCreated?: (info: { id: string; name: string }) => void;
  trigger?: React.ReactNode;
}

const AddCourseDialog = ({ onAddCourse, onCourseCreated, trigger }: AddCourseDialogProps) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [courseId, setCourseId] = useState<string | null>(null);
  const [showHolesDialog, setShowHolesDialog] = useState(false);
  const [currentHole, setCurrentHole] = useState(1);
  const [holePars, setHolePars] = useState<Record<number, number>>({});
  const [holeHandicaps, setHoleHandicaps] = useState<Record<number, number>>({});
  const [savingHoles, setSavingHoles] = useState(false);

  // Initialize form
  const form = useForm<CourseFormValues>({
    resolver: zodResolver(courseFormSchema),
    defaultValues: {
      name: "",
      location: "",
      holesCount: 18,
      availableTees: ["red", "white", "blue"]
    }
  });
  
  // Parse location into city and state
  const parseLocation = (location: string): { city: string, state: string } => {
    const parts = location.split(',').map(part => part.trim());
    // Default to empty string if part doesn't exist
    return {
      city: parts[0] || "",
      state: parts[1] || ""
    };
  };

  const handleAddCourse = async (values: CourseFormValues) => {
    setLoading(true);
    try {
      // Parse location into city and state
      const { city, state } = parseLocation(values.location);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication Error",
          description: "You must be logged in to add a course.",
          variant: "destructive"
        });
        setLoading(false);
        setShowAddDialog(false);
        return;
      }
      
      // Insert the course into the database
      const { data: course, error } = await supabase
        .from('courses')
        .insert({
          name: values.name,
          city,
          state,
          holes_count: values.holesCount,
          par: values.par,
          available_tees: values.availableTees,
          user_id: user.id
        })
        .select()
        .single();
      
      if (error) {
        throw error;
      }
      
      // Add course to user's courses
      const { error: linkError } = await supabase
        .from('profile_course_link')
        .insert({
          profile_id: user.id,
          course_id: course.id,
          is_favorite: true
        });
      
      if (linkError) {
        throw linkError;
      }
      
      toast({
        title: "Course Added",
        description: `${values.name} has been added to your courses.`,
      });
      
      // Store course ID for hole setup
      setCourseId(course.id);
      
      // Ask if user wants to set up holes
      setShowHolesDialog(true);
      
    } catch (error) {
      toast({
        title: "Error Adding Course",
        description: getErrorMessage(error) || "An error occurred while adding the course.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setShowAddDialog(false);
      onAddCourse();
    }
  };
  
  // Handle hole par and handicap changes
  const handleHoleParChange = (hole: number, par: number) => {
    setHolePars(prev => ({ ...prev, [hole]: par }));
  };
  
  const handleHoleHandicapChange = (hole: number, handicap: number) => {
    setHoleHandicaps(prev => ({ ...prev, [hole]: handicap }));
  };
  
  // Save hole details to database
  const saveHoleDetails = async () => {
    if (!courseId) return;
    
    setSavingHoles(true);
    
    try {
      const holesCount = form.getValues().holesCount || 18;
      
      // Prepare holes data for insertion
      const holes = Array.from({ length: holesCount }, (_, i) => {
        const holeNumber = i + 1;
        return {
          course_id: courseId,
          hole_number: holeNumber,
          par: holePars[holeNumber] || 4, // Default to par 4
          handicap: holeHandicaps[holeNumber] || null
        };
      });
      
      const { error } = await supabase
        .from('holes')
        .insert(holes);
      
      if (error) throw error;
      
      toast({
        title: "Holes Added",
        description: `Added details for ${holesCount} holes.`,
      });
      
    } catch (error) {
      toast({
        title: "Error Adding Holes",
        description: getErrorMessage(error) || "An error occurred while adding hole details.",
        variant: "destructive"
      });
    } finally {
      setSavingHoles(false);
      setShowHolesDialog(false);
      // Refresh course list
      onAddCourse();
      onCourseCreated?.({ id: courseId!, name: form.getValues().name });
    }
  };

  // Skip hole details setup but still create default holes
  const skipHoleSetup = async () => {
    if (!courseId) return;
    
    setSavingHoles(true);
    
    try {
      const holesCount = form.getValues().holesCount || 18;
      
      // Create default holes with standard par values
      const defaultHoles = Array.from({ length: holesCount }, (_, i) => {
        const holeNumber = i + 1;
        // Assign standard par values: par 3 for short holes, par 5 for long holes, par 4 for the rest
        // This is a simplified approach - typically holes 3, 6, 12, 17 might be par 3s and holes 1, 5, 10, 15 might be par 5s
        let defaultPar = 4;
        if ([3, 6, 12, 17].includes(holeNumber)) {
          defaultPar = 3;
        } else if ([1, 5, 10, 15].includes(holeNumber)) {
          defaultPar = 5;
        }
        
        return {
          course_id: courseId,
          hole_number: holeNumber,
          par: defaultPar,
          handicap: null
        };
      });
      
      const { error } = await supabase
        .from('holes')
        .insert(defaultHoles);
      
      if (error) throw error;
      
      toast({
        title: "Course Created",
        description: `Created ${holesCount} holes with default par values. You can edit these later.`,
      });
      
    } catch (error) {
      toast({
        title: "Error Creating Holes",
        description: getErrorMessage(error) || "An error occurred while creating default holes.",
        variant: "destructive"
      });
    } finally {
      setSavingHoles(false);
      setShowHolesDialog(false);
      // Refresh course list
      onAddCourse();
      onCourseCreated?.({ id: courseId!, name: form.getValues().name });
    }
  };

  return (
    <>
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogTrigger asChild>
          {trigger ?? (
            <Button
              className="w-full flex items-center justify-center gap-2 bg-forest hover:bg-forest/90 text-white mt-4"
              variant="outline"
            >
              <PlusCircle className="h-4 w-4" />
              <span>Add New Course</span>
            </Button>
          )}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add a New Course</DialogTitle>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleAddCourse)} className="space-y-4 py-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Course Name</FormLabel>
                    <FormControl>
                      <Input placeholder="Augusta National" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="Augusta, GA" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="holesCount"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Number of Holes</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={9}
                        max={27}
                        {...field}
                        onChange={e => field.onChange(parseInt(e.target.value))}
                        value={field.value || 18}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="par"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Course Par (optional)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        placeholder="72"
                        {...field}
                        onChange={e => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                        value={field.value || ''}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Button 
                type="submit" 
                className="w-full bg-forest hover:bg-forest/90 text-white"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Adding...
                  </>
                ) : (
                  "Add Course"
                )}
              </Button>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      
      {/* Hole Details Dialog */}
      <AlertDialog open={showHolesDialog} onOpenChange={setShowHolesDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Add Hole Details</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to set the par and handicap for each hole? This is optional and can be done later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {showHolesDialog && (
            <div className="py-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Hole {currentHole}</h3>
                <div className="text-sm text-muted-foreground">
                  {currentHole} of {form.getValues().holesCount || 18}
                </div>
              </div>
              <div className="w-full bg-muted rounded-full h-1.5 mb-4">
                <div
                  className="bg-forest h-1.5 rounded-full transition-all"
                  style={{ width: `${(currentHole / (form.getValues().holesCount || 18)) * 100}%` }}
                />
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="holePar">Par</Label>
                  <Input 
                    id="holePar" 
                    type="number" 
                    min={3} 
                    max={6} 
                    value={holePars[currentHole] || 4}
                    onChange={(e) => handleHoleParChange(currentHole, parseInt(e.target.value))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="holeHandicap">Handicap (optional)</Label>
                  <Input 
                    id="holeHandicap" 
                    type="number" 
                    min={1} 
                    max={18} 
                    value={holeHandicaps[currentHole] || ''}
                    onChange={(e) => handleHoleHandicapChange(currentHole, parseInt(e.target.value))}
                  />
                </div>
                
                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentHole(prev => Math.max(1, prev - 1))}
                    disabled={currentHole === 1}
                  >
                    Previous
                  </Button>
                  
                  <Button
                    onClick={() => {
                      if (currentHole < (form.getValues().holesCount || 18)) {
                        setCurrentHole(prev => prev + 1);
                      } else {
                        saveHoleDetails();
                      }
                    }}
                  >
                    {currentHole < (form.getValues().holesCount || 18) ? "Next" : "Save All"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={skipHoleSetup}
              disabled={savingHoles}
              className="bg-background text-foreground border border-input hover:bg-accent"
            >
              {savingHoles ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating default holes...
                </>
              ) : (
                "Use Default Pars"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AddCourseDialog;
